import React, { useEffect, useState } from 'react';
import { Modal } from '@/components';
import { useForm, useController } from 'react-hook-form';
import {
  BoxV2 as Box,
  Flex,
  Button,
  TextInput,
  MultiSelector,
} from 'portal-commons';
import { CampaignListItem } from '@/apis/core/types';
import { SelectItem } from '@/types';
import { campaignController } from '@/apis/core';
import {
  isValidInput,
  createMaxLengthValidator,
  createRequiredValidator,
} from '@/utils/validator';

interface Props {
  open: boolean;
  campaign: Pick<CampaignListItem, 'uid' | 'cspUid' | 'brandName'>;
  categories: SelectItem[];
  onClose: (reload: boolean) => void;
}

interface FormValues {
  explanation: string;
  rejectionCategory: string[];
}

const CspCampaignDeclineModal: React.FC<Props> = ({
  open,
  campaign,
  categories = [],
  onClose,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: {
      explanation: '',
      rejectionCategory: [],
    },
  });

  const {
    field: { value: category, onChange: setCategory },
    fieldState: { error: categoryError },
  } = useController({
    control,
    name: 'rejectionCategory',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a category'),
      },
    },
  });

  const {
    field: {
      value: explanation,
      onChange: setExplanation,
      onBlur: validateExplanation,
    },
    fieldState: { error: explanationError },
  } = useController({
    control,
    name: 'explanation',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please enter Explanation'),
        maxLength: createMaxLengthValidator(2048),
        pattern: (value) => isValidInput(value) || 'Invalid input',
      },
    },
  });

  const disabledSubmit =
    isProcessing || Object.values(errors).some((error) => !!error?.message);

  const onValidHandler = async (data: FormValues) => {
    setIsProcessing(true);

    const response = await campaignController.rejectCampaignAsPrimaryDca(
      campaign.uid,
      data
    );
    if (response) {
      onClose(true);
    }

    setIsProcessing(false);
  };

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  return (
    <Modal open={open} testid="campaignDeclineDialog">
      <Box
        as="h3"
        sx={{
          py: 's',
          fontSize: 'H600',
          fontWeight: 600,
          lineHeight: 'normal',
          color: 't.black80',
          textAlign: 'center',
        }}
      >
        Decline Campaign
      </Box>
      <form noValidate onSubmit={handleSubmit(onValidHandler)}>
        <Flex
          sx={{
            flexDirection: 'column',
            gap: 18,
            my: 'sx',
          }}
        >
          <Box
            as="span"
            sx={{ fontSize: 'H100', color: 't.grey500', mb: '-10px' }}
          >
            * Indicates a Required Field
          </Box>
          <TextInput readOnly label="Campaign ID" value={campaign.uid} />
          <TextInput
            readOnly
            label="Brand DBA Name"
            value={campaign.brandName || 'N/A'}
          />
          <TextInput readOnly label="CSP ID" value={campaign.cspUid} />
          <Box sx={{ width: '100%', mt: '-4px' }}>
            <MultiSelector
              disabled={isProcessing}
              options={categories}
              data-testid="rejectCategory"
              label="Category *"
              placeholder="Category"
              value={category[0]}
              error={!!categoryError?.message}
              helperText={categoryError?.message}
              onChange={(value: string) => {
                if (value.length) {
                  const categoryArray = value.split(',');
                  setCategory(categoryArray);
                } else {
                  setCategory([]);
                }
              }}
            />
          </Box>
          <TextInput
            required
            data-testid="rejectExplanation"
            label="Explanation"
            placeholder="Please enter Explanation"
            disabled={isProcessing}
            value={explanation}
            maxLength={2048}
            error={!!explanationError?.message}
            note={explanationError?.message}
            onChange={setExplanation}
            onBlur={validateExplanation}
          />
          <Flex sx={{ py: 30, gap: 18, justifyContent: 'center' }}>
            <Button
              variant="outline"
              data-testid="rejectCampaignCancelButton"
              disabled={isProcessing}
              onClick={() => onClose(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={disabledSubmit}
              data-testid="rejectCampaignDeclineButton"
              type="submit"
            >
              Decline
            </Button>
          </Flex>
        </Flex>
      </form>
    </Modal>
  );
};

export default CspCampaignDeclineModal;
